import { Color, Vector3 } from "three";
import { EnvPreset } from "./EnvPreset";
import type { DynamicSkyParams } from "./materials/dynamicSkyMaterial";

// Re-export for backwards compatibility
export {
  TONE_MAPPING_ALGORITHM,
  HIGHARC_ACES_FILMIC_DIVISOR_A,
  HIGHARC_ACES_FILMIC_DIVISOR_B,
  HIGHARC_ACES_FILMIC_DIVISOR_C,
} from "./toneMappingConstants";

interface HemisphereLightParams {
  position: Vector3;
  intensity: number;
  color: Color;
  groundColor: Color;
}

interface DirectionalLightParams {
  position: Vector3;
  intensity: number;
  color: Color;
}

interface AmbientLightParams {
  intensity: number;
  color: Color;
}

export interface FogParams {
  color: Color;
  near: number;
  far: number;
  radialFogNear?: number;
  radialFogFar?: number;
}

export interface InteriorLightSettings {
  decayMultiplier: number;
  intensityMultiplier: number;
}

/**
 * Data for an interior point light used in offset map generation.
 */
export interface InteriorLightData {
  x: number;
  y: number;
  z: number;
  color: number;
  intensity: number;
  distance: number;
  decay: number;
}

export interface SceneLightSettings {
  toneMappingEnabled: boolean;
  toneMappingExposure: number;
  hemisphereLight: Partial<HemisphereLightParams>;
  directionalLight: Partial<DirectionalLightParams>;
  ambientLight: Partial<AmbientLightParams>;
  interiorLightSettings?: Partial<InteriorLightSettings>;
  interiorAmbientLight?: Partial<AmbientLightParams>;
  interiorNoProbesAmbientLight?: Partial<AmbientLightParams>;
  sky?: Partial<DynamicSkyParams>;
  fog: FogParams;
  probeIrradianceSaturationMultiplier: number; // Between 0 and 1.
  lightProbeSkyHemiLightFactor: number; // Between 0 and 1.
  lightProbeSkyHemiLightHorizontal: number; // Between 0 and 1, controls the inclination of the hemisphere light used for generating lightprobes.
  exteriorEnvMap: string[] | string; // May be either an array of cube map face urls, or a single url pointing to a single equirect texture.
  exteriorEnvMapEXR: boolean; // Set to true if the env map is in EXR format. In this case it must be a single equirect texture.
}

export const WHITE_BOX_PRESET: SceneLightSettings = {
  toneMappingEnabled: false,
  toneMappingExposure: 1.0,
  hemisphereLight: {
    position: new Vector3(0, 0, 1),
    intensity: 0.7,
    color: new Color(0xffffff),
    groundColor: new Color(0xd5d5d5),
  },
  directionalLight: {
    position: new Vector3(-400, -1500, 2000),
    intensity: 0.92,
    color: new Color(0xfefef6),
  },
  ambientLight: {
    intensity: 0.1,
    color: new Color(0xedefff),
  },
  interiorAmbientLight: {
    intensity: 0.1,
    color: new Color(0xedefff),
  },
  interiorNoProbesAmbientLight: {
    intensity: 0.6,
    color: new Color(0xedefff),
  },
  interiorLightSettings: {
    decayMultiplier: 1.0,
    intensityMultiplier: 1.0,
  },
  sky: {
    topColor: new Color(0xe6e6e6),
    horizonColor: new Color(0xffffff),
    bottomColor: new Color(0xffffff),
    horizonSolidWidth: 0.009999999999999967,
    horizonGradientWidth: 0.7,
    horizonInclination: -0.03,
    sunCenterSize: 3.500000000000003,
    sunCenterColor: new Color(0xffffff),
    sunCenterBlendAlpha: 0,
    sunHaloSize: 0.18999999999999992,
    sunHaloBlendAlpha: 0,
    sunHaloColor: new Color(0xfdfcec),
    sunHazeSize: 30,
    sunHazeBlendAlpha: 0,
    sunHazeColor: new Color(0xfaef20),
  },
  lightProbeSkyHemiLightFactor: 0.25,
  lightProbeSkyHemiLightHorizontal: 0.0,
  fog: {
    color: new Color(0xffffff),
    near: 200,
    far: 8000,
  },
  probeIrradianceSaturationMultiplier: 0.0,
  exteriorEnvMap: [
    "/static/textures/envmaps/whitebox/px.jpg",
    "/static/textures/envmaps/whitebox/nx.jpg",
    "/static/textures/envmaps/whitebox/py.jpg",
    "/static/textures/envmaps/whitebox/ny.jpg",
    "/static/textures/envmaps/whitebox/pz.jpg",
    "/static/textures/envmaps/whitebox/nz.jpg",
  ],
  exteriorEnvMapEXR: false,
};

export const envPresetSceneLights: { [time in EnvPreset]: SceneLightSettings } = {
  [EnvPreset.MORNING]: {
    toneMappingEnabled: false,
    toneMappingExposure: 1.0,
    hemisphereLight: {
      position: new Vector3(0, 0, 1),
      intensity: 0.9,
      color: new Color(0xcce9e9),
      groundColor: new Color(0x665e4d),
    },
    directionalLight: {
      position: new Vector3(1600, -1500, 1000),
      intensity: 0.9,
      color: new Color(0xfbfbef),
    },
    ambientLight: {
      intensity: 0.05,
      color: new Color(0xededff),
    },
    interiorAmbientLight: {
      intensity: 0.1,
      color: new Color(0xedefff),
    },
    interiorNoProbesAmbientLight: {
      intensity: 0.6,
      color: new Color(0xedefff),
    },
    interiorLightSettings: {
      decayMultiplier: 1.0,
      intensityMultiplier: 1.0,
    },
    sky: {
      topColor: new Color(0xd4e3e7),
      topMagnitude: 1.0,
      horizonColor: new Color(0xe6ecdf),
      horizonMagnitude: 1.0,
      bottomColor: new Color(0xeeeedd),
      bottomMagnitude: 1.0,
      horizonSolidWidth: 0.1,
      horizonGradientWidth: 0.5,
      horizonInclination: -0.03,
      sunCenterSize: 3,
      sunCenterColor: new Color(0xfcf5dc),
      sunCenterMagnitude: 1.0,
      sunCenterBlendAlpha: 0.9,
      sunHaloSize: 0.10000000000000006,
      sunHaloBlendAlpha: 1.3130000000000006,
      sunHaloColor: new Color(0xe6eedd),
      sunHaloMagnitude: 1.0,
      sunHazeSize: 30,
      sunHazeBlendAlpha: 0.15,
      sunHazeColor: new Color(0xfaef20),
      sunHazeMagnitude: 1.0,
    },
    lightProbeSkyHemiLightFactor: 0.4,
    lightProbeSkyHemiLightHorizontal: 0.0,
    fog: {
      color: new Color(0xd9ddd3),
      near: 2000,
      far: 6500,
      radialFogNear: 950,
      radialFogFar: 3750,
    },
    probeIrradianceSaturationMultiplier: 0.85,
    exteriorEnvMap: [
      "/static/textures/envmaps/morning_field/px.jpg",
      "/static/textures/envmaps/morning_field/nx.jpg",
      "/static/textures/envmaps/morning_field/py.jpg",
      "/static/textures/envmaps/morning_field/ny.jpg",
      "/static/textures/envmaps/morning_field/pz.jpg",
      "/static/textures/envmaps/morning_field/nz.jpg",
    ],
    exteriorEnvMapEXR: false,
  },

  [EnvPreset.MORNING_HDR]: {
    toneMappingEnabled: true,
    toneMappingExposure: 0.75,
    hemisphereLight: {
      position: new Vector3(0, 0, 1),
      intensity: 2.2,
      color: new Color(0xace9ec),
      groundColor: new Color(0x665e4d),
    },
    directionalLight: {
      position: new Vector3(1600, -1500, 1000),
      intensity: 1.65,
      color: new Color(0xfbfbef),
    },
    ambientLight: {
      intensity: 0.1,
      color: new Color(0xebf6ff),
    },
    interiorAmbientLight: {
      intensity: 0.1,
      color: new Color(0xedefff),
    },
    interiorNoProbesAmbientLight: {
      intensity: 0.5, // Suggested value: ~0.4
      color: new Color(0xedefff),
    },
    interiorLightSettings: {
      decayMultiplier: 2.0, // Suggested value: ~1.5
      intensityMultiplier: 3.5, // Suggested value: ~2.0
    },
    sky: {
      topColor: new Color(0x79dafb),
      topMagnitude: 1,
      horizonColor: new Color(0xc2ecfc),
      horizonMagnitude: 1.4,
      bottomColor: new Color(0xededde),
      bottomMagnitude: 1.2,
      horizonSolidWidth: 0.05,
      horizonGradientWidth: 0.45,
      horizonInclination: -0.3,
      sunCenterSize: 3,
      sunCenterColor: new Color(0xf5f195),
      sunCenterMagnitude: 40,
      sunCenterBlendAlpha: 1,
      sunHaloSize: 0.05,
      sunHaloBlendAlpha: 0.5,
      sunHaloColor: new Color(0xe6fae5),
      sunHaloMagnitude: 8,
      sunHazeSize: 30,
      sunHazeBlendAlpha: 0.15,
      sunHazeColor: new Color(0xfff64b),
      sunHazeMagnitude: 1,
    },
    lightProbeSkyHemiLightFactor: 0.6,
    lightProbeSkyHemiLightHorizontal: 0.8,
    fog: {
      color: new Color(0.77, 0.91, 0.91),
      near: 2000,
      far: 6500,
      radialFogNear: 950,
      radialFogFar: 6500,
    },
    probeIrradianceSaturationMultiplier: 0.85,
    exteriorEnvMap: [
      "/static/textures/envmaps/morning_field/px.jpg",
      "/static/textures/envmaps/morning_field/nx.jpg",
      "/static/textures/envmaps/morning_field/py.jpg",
      "/static/textures/envmaps/morning_field/ny.jpg",
      "/static/textures/envmaps/morning_field/pz.jpg",
      "/static/textures/envmaps/morning_field/nz.jpg",
    ],
    exteriorEnvMapEXR: false,
  },

  [EnvPreset.MIDDAY]: {
    toneMappingEnabled: false,
    toneMappingExposure: 1.0,
    hemisphereLight: {
      position: new Vector3(0, 0, 1),
      intensity: 0.7,
      color: new Color(0xe0f7f8),
      groundColor: new Color(0xa7a78b),
    },
    directionalLight: {
      position: new Vector3(-400, -1500, 2000),
      intensity: 0.92,
      color: new Color(0xfefef6),
    },
    ambientLight: {
      intensity: 0.1,
      color: new Color(0xedefff),
    },
    interiorAmbientLight: {
      intensity: 0.1,
      color: new Color(0xedefff),
    },
    interiorNoProbesAmbientLight: {
      intensity: 0.6,
      color: new Color(0xedefff),
    },
    interiorLightSettings: {
      decayMultiplier: 1.0,
      intensityMultiplier: 1.0,
    },
    sky: {
      topColor: new Color(0xa8e3e6),
      horizonColor: new Color(0xe1f4f5),
      bottomColor: new Color(0xffffff),
      horizonSolidWidth: 0.009999999999999967,
      horizonGradientWidth: 0.32999999999999974,
      horizonInclination: -0.03,
      sunCenterSize: 3.500000000000003,
      sunCenterColor: new Color(0xffffff),
      sunCenterBlendAlpha: 1.5999999999999994,
      sunHaloSize: 0.18999999999999992,
      sunHaloBlendAlpha: 0.9999999999999999,
      sunHaloColor: new Color(0xfdfcec),
      sunHazeSize: 30,
      sunHazeBlendAlpha: 0.15,
      sunHazeColor: new Color(0xfaef20),
    },
    lightProbeSkyHemiLightFactor: 0.25,
    lightProbeSkyHemiLightHorizontal: 0.0,
    fog: {
      color: new Color(0xe0f4f5),
      near: 5,
      far: 9000,
    },
    probeIrradianceSaturationMultiplier: 0.8,
    exteriorEnvMap: [
      "/static/textures/envmaps/field/px.jpg",
      "/static/textures/envmaps/field/nx.jpg",
      "/static/textures/envmaps/field/py.jpg",
      "/static/textures/envmaps/field/ny.jpg",
      "/static/textures/envmaps/field/pz.jpg",
      "/static/textures/envmaps/field/nz.jpg",
    ],
    exteriorEnvMapEXR: false,
  },

  [EnvPreset.EVENING]: {
    toneMappingEnabled: false,
    toneMappingExposure: 1.0,
    hemisphereLight: {
      position: new Vector3(0, 0, 1),
      intensity: 0.0,
      color: new Color(0xf0f0c1),
      groundColor: new Color(0x665e4d),
    },
    directionalLight: {
      position: new Vector3(-2000, -1500, 350),
      intensity: 0.6,
      color: new Color(0xfbf1d1),
    },
    ambientLight: {
      intensity: 0.05,
      color: new Color(0xeee8ff),
    },
    interiorAmbientLight: {
      intensity: 0.1,
      color: new Color(0xedefff),
    },
    interiorNoProbesAmbientLight: {
      intensity: 0.6,
      color: new Color(0xedefff),
    },
    interiorLightSettings: {
      decayMultiplier: 1.0,
      intensityMultiplier: 1.0,
    },
    sky: {
      topColor: new Color(0xd9cdfe),
      horizonColor: new Color(0xebd1ad),
      bottomColor: new Color(0xebdcad),
      horizonSolidWidth: 0.05000000000000011,
      horizonGradientWidth: 0.7500000000000002,
      horizonInclination: -0.03,
      sunCenterSize: 3.900000000000001,
      sunCenterColor: new Color(0xf3ca81),
      sunCenterBlendAlpha: 0.8,
      sunHaloSize: 7.099999999999999,
      sunHaloBlendAlpha: 1.0999999999999839,
      sunHaloColor: new Color(0xebae6f),
      sunHazeSize: 10,
      sunHazeBlendAlpha: 0.5,
      sunHazeColor: new Color(0xdcbb57),
      sunAlphaBlendingFactor: 0.5, // Use a degree of additive blending for the sun.
    },
    lightProbeSkyHemiLightFactor: 0.5,
    lightProbeSkyHemiLightHorizontal: 0.0,
    fog: {
      color: new Color(0xf2d9b5),
      near: 5,
      far: 9000,
    },
    probeIrradianceSaturationMultiplier: 0.8,
    exteriorEnvMap: [
      "/static/textures/envmaps/evening_field/px.jpg",
      "/static/textures/envmaps/evening_field/nx.jpg",
      "/static/textures/envmaps/evening_field/py.jpg",
      "/static/textures/envmaps/evening_field/ny.jpg",
      "/static/textures/envmaps/evening_field/pz.jpg",
      "/static/textures/envmaps/evening_field/nz.jpg",
    ],
    exteriorEnvMapEXR: false,
  },

  [EnvPreset.WHITEBOX]: WHITE_BOX_PRESET,

  [EnvPreset.WHITEBOX_LOW_FOG]: {
    ...WHITE_BOX_PRESET,
    fog: {
      color: new Color(0xffffff),
      near: 1000,
      far: 20000,
    },
  },
};
